// src/pages/admin/customers/Customers.jsx
import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import AddCustomerModal from '../../../components/modals/AddCustomerModal'
import EditCustomerModal from '../../../components/modals/EditCustomerModal'
import ViewCustomerModal from '../../../components/modals/ViewCustomerModal'
import DeleteCustomerModal from '../../../components/modals/DeleteCustomerModal'
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  Filter,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Users,
  User,
  Phone,
  Building,
  Award,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Package
} from 'lucide-react'
import CustomerService from '../../../services/customerService'
import BranchService from '../../../services/branchService'
import AuthService from '../../../services/authService'
import { CustomerModel } from '../../../utils/customerModel'
import InlineLoader from '../../../components/ui/InlineLoader'
import ButtonSpinner from '../../../components/ui/ButtonSpinner'

// Enhanced Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-in slide-in-from-right-full duration-300 ${
    type === 'success' 
      ? 'bg-green-500 text-white' 
      : type === 'error'
      ? 'bg-red-500 text-white'
      : type === 'warning'
      ? 'bg-yellow-500 text-white'
      : 'bg-blue-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : 
     type === 'error' ? <XCircle className="h-5 w-5" /> : 
     <AlertCircle className="h-5 w-5" />}
    <span className="font-medium">{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2 hover:bg-black hover:bg-opacity-20 rounded p-1">
      <X className="h-4 w-4" />
    </button>
  </div>
)

// Enhanced Stats Card Component
const StatsCard = ({ icon: Icon, title, value, color = "orange" }) => (
  <Card className="hover:shadow-md transition-shadow duration-200">
    <CardContent className="p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
)

export const Customers = () => {
  const ITEMS_PER_PAGE = 100
  const [customers, setCustomers] = useState([])        // current page data from API
  const [filteredCustomers, setFilteredCustomers] = useState([])  // client-filtered view
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)       // total records reported by API
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [branchFilter, setBranchFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [loyaltyFilter, setLoyaltyFilter] = useState('')
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [actionLoading, setActionLoading] = useState({})
  const [toast, setToast] = useState(null)
  const [branches, setBranches] = useState([])
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    totalLoyaltyPoints: 0
  })

  // Get current user
  const currentUser = AuthService.getCurrentUser()

  // Helper function to check if user is branch-restricted (manager)
  const isBranchRestricted = () => {
    if (!currentUser) return false
    const restrictedRoles = ['manager']
    return restrictedRoles.includes(currentUser.role?.toLowerCase())
  }

  // Get user's branch ID
  const getUserBranchId = () => {
    if (!isBranchRestricted()) return null
    return currentUser?.branchId || currentUser?.branch?._id || currentUser?.branch || null
  }

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  // Helper function to safely get property value
  const safeGet = (obj, path, defaultValue = '') => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  // Fetch a single page from the server
  const fetchPage = async (page = 1, showRefreshToast = false) => {
    console.log(`🔄 Fetching page ${page}...`)
    setLoading(true)
    setError(null)

    try {
      const [customersResponse, branchesResponse] = await Promise.all([
        CustomerService.getAllCustomers({ page, limit: ITEMS_PER_PAGE }),
        page === 1 ? BranchService.getAllBranches() : Promise.resolve(null)
      ])

      if (customersResponse?.success) {
        const customersData = safeGet(customersResponse, 'data.customers', [])
        // Try common shapes: data.pagination.total | data.total | data.count
        const apiTotal =
          safeGet(customersResponse, 'data.pagination.total', null) ??
          safeGet(customersResponse, 'data.total', null) ??
          safeGet(customersResponse, 'data.count', null)

        const validCustomers = Array.isArray(customersData)
          ? customersData.filter(c => c && c._id)
          : []

        console.log(`✅ Page ${page}: ${validCustomers.length} customers (API total: ${apiTotal})`)

        setCustomers(validCustomers)
        setCurrentPage(page)

        // Use API-reported total if available, otherwise estimate from current page
        if (apiTotal !== null && apiTotal !== undefined) {
          setTotalCount(Number(apiTotal))
        } else {
          // fallback: if we got a full page assume there might be more
          setTotalCount(prev => {
            const minKnown = (page - 1) * ITEMS_PER_PAGE + validCustomers.length
            return Math.max(prev, minKnown)
          })
        }

        const calculatedStats = {
          total: apiTotal ?? validCustomers.length,
          active: validCustomers.filter(c => c.isActive).length,
          inactive: validCustomers.filter(c => !c.isActive).length,
          totalLoyaltyPoints: validCustomers.reduce((sum, c) => sum + (c.pointsBalance || c.loyaltyPoints || 0), 0)
        }
        setStats(calculatedStats)
      } else {
        console.warn('Customers API request unsuccessful:', customersResponse)
        setError('Failed to fetch customers')
      }

      if (branchesResponse?.success) {
        const branchesData = safeGet(branchesResponse, 'data.branches', [])
        setBranches(branchesData.filter(branch => branch.isActive))
      }

      if (showRefreshToast) showToast('Data refreshed successfully', 'success')
    } catch (err) {
      console.error('❌ Error fetching customers:', err)
      setError(err.message || 'Failed to fetch customers')
      if (showRefreshToast) showToast('Error refreshing data', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Refresh button handler – goes back to page 1
  const handleRefresh = () => {
    setCurrentPage(1)
    fetchPage(1, true)
  }

  // Alias used by modal callbacks
  const fetchData = () => fetchPage(currentPage)

  // Filter customers based on search and filters
  const applyFilters = () => {
    let filtered = [...customers]
    
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(customer => 
        customer.name?.toLowerCase().includes(searchLower) ||
        customer.mobileNumber?.includes(searchTerm)
      )
    }
    
    if (branchFilter) {
      filtered = filtered.filter(customer => 
        customer.branch?._id === branchFilter
      )
    }
    
    if (statusFilter) {
      const isActive = statusFilter === 'active'
      filtered = filtered.filter(customer => customer.isActive === isActive)
    }
    
    if (loyaltyFilter) {
      filtered = filtered.filter(customer => {
        const tier = CustomerModel.getLoyaltyTier(customer.pointsBalance, customer.loyaltyPoints)
        return tier.name.toLowerCase() === loyaltyFilter.toLowerCase()
      })
    }
    
    setFilteredCustomers(filtered)
    setCurrentPage(1) // reset to first page whenever filters change
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    setBranchFilter('')
    setStatusFilter('')
    setLoyaltyFilter('')
  }

  // Load page 1 on mount
  useEffect(() => {
    fetchPage(1)
  }, [])

  // Apply client-side filters whenever the current page's data or filter values change
  useEffect(() => {
    applyFilters()
  }, [customers, searchTerm, branchFilter, statusFilter, loyaltyFilter])

  // Total pages based on API-reported total
  const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE))

  // For server-side pagination paginatedCustomers IS the filtered list (already one page)
  const paginatedCustomers = filteredCustomers

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return
    fetchPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Handle customer actions
  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer)
    setShowViewModal(true)
  }

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer)
    setShowEditModal(true)
  }

  const handleDeleteCustomer = (customer) => {
    setSelectedCustomer(customer)
    setShowDeleteModal(true)
  }

  // Handle customer events
  const handleCustomerCreated = (newCustomer) => {
    console.log('👥 New customer created:', newCustomer)
    showToast('Customer created successfully!', 'success')
    fetchData()
  }

  const handleCustomerUpdated = (updatedCustomer) => {
    console.log('👥 Customer updated:', updatedCustomer)
    showToast('Customer updated successfully!', 'success')
    fetchData()
  }

  const handleCustomerDeleted = (deletedCustomer) => {
    console.log('👥 Customer deleted:', deletedCustomer)
    showToast('Customer deleted successfully!', 'success')
    fetchData()
  }

  // Toggle customer status
  const handleToggleStatus = async (customer) => {
    const customerId = customer._id
    try {
      setActionLoading(prev => ({ ...prev, [customerId]: true }))
      
      const newStatus = !customer.isActive
      await CustomerService.updateCustomerStatus(customerId, newStatus)
      
      showToast(
        `Customer ${newStatus ? 'activated' : 'deactivated'} successfully!`, 
        'success'
      )
      fetchData()
    } catch (error) {
      console.error('❌ Error updating customer status:', error)
      showToast('Failed to update customer status', 'error')
    } finally {
      setActionLoading(prev => ({ ...prev, [customerId]: false }))
    }
  }

  if (loading) {
    return (
      <InlineLoader title="Loading customers..." py="h-64" />
    )
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}

      {/* Enhanced Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Customers</h1>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            onClick={handleRefresh}
            variant="outline"
            className="flex items-center space-x-2 border-gray-300 hover:bg-gray-50"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Customer
          </Button>
        </div>
      </div>

      {/* Enhanced Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          icon={Users}
          title="Total Customers"
          value={stats.total.toLocaleString()}
          color="blue"
        />
        <StatsCard
          icon={CheckCircle}
          title="Active Customers"
          value={stats.active.toLocaleString()}
          color="green"
        />
        <StatsCard
          icon={XCircle}
          title="Inactive Customers"
          value={stats.inactive.toLocaleString()}
          color="red"
        />
        <StatsCard
          icon={Award}
          title="Total Loyalty Points"
          value={stats.totalLoyaltyPoints.toLocaleString()}
          color="purple"
        />
      </div>

      {/* Enhanced Filters Card */}
      <Card className="shadow-sm">
        <CardContent className="p-6">
          {/* Filter Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filter Customers</h2>
            </div>
            {(searchTerm || branchFilter || statusFilter || loyaltyFilter) && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="text-orange-500 border-orange-500 hover:bg-orange-50"
                size="sm"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search by name or mobile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
              />
            </div>

            {/* Branch Filter */}
            <div className="relative">
              <select
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="">All Branches</option>
                {branches.map(branch => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Loyalty Filter */}
            <div className="relative">
              <select
                value={loyaltyFilter}
                onChange={(e) => setLoyaltyFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="">All Tiers</option>
                <option value="bronze">Bronze</option>
                <option value="silver">Silver</option>
                <option value="gold">Gold</option>
                <option value="platinum">Platinum</option>
              </select>
            </div>
          </div>
          
          {/* Results Count */}
          <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md">
            Showing <span className="font-medium text-gray-900">{filteredCustomers.length}</span> on this page &nbsp;·&nbsp; Page <span className="font-medium text-gray-900">{currentPage}</span> of <span className="font-medium text-gray-900">{totalPages}</span> &nbsp;·&nbsp; <span className="font-medium text-gray-900">{totalCount.toLocaleString()}</span> total customers
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Customer List */}
      <Card className="shadow-sm">
        <CardHeader className="border-b border-gray-200">
          <CardTitle className="flex items-center space-x-2">
            <Package className="h-5 w-5 text-orange-600" />
            <span>Customer Directory</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {error ? (
            <div className="text-center py-12 px-6">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Customers</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button 
                onClick={handleRefresh} 
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="text-center py-12 px-6">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {customers.length === 0 ? 'No customers found' : 'No customers match your filters'}
              </h3>
              <p className="text-gray-600 mb-4">
                {customers.length === 0 
                  ? 'Get started by creating your first customer.' 
                  : 'Try adjusting your search criteria.'}
              </p>
              {customers.length === 0 && (
                <Button
                  onClick={() => setShowAddModal(true)}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Customer
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full min-w-full bg-white">
                  {/* Table Header */}
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mobile
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Branch
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Age
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer Type
                      </th>
                      {/* <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Loyalty
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Orders
                      </th> */}
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  {/* Table Body */}
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedCustomers.map((customer) => {
                      const formattedCustomer = CustomerService.formatCustomerForDisplay(customer)
                      return (
                        <tr key={customer._id} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-orange-100 rounded-lg">
                                <User className="h-4 w-4 text-orange-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{customer.name}</p>
                                <p className="text-sm text-gray-500">ID: {customer._id.slice(-8).toUpperCase()}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Phone className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900">{formattedCustomer.formattedMobile}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Building className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900">{customer.branch?.name || 'Not assigned'}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900">
                                {formattedCustomer.age ? `${formattedCustomer.age} years` : 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                              customer.customerType === 'vip'
                                ? 'bg-gradient-to-r from-yellow-100 to-amber-100 text-amber-800 border border-amber-200'
                                : 'bg-blue-100 text-blue-800 border border-blue-200'
                            }`}>
                              {customer.customerType === 'vip' ? (
                                <>
                                  <Award className="h-3 w-3 mr-1" />
                                  VIP
                                </>
                              ) : (
                                <>
                                  <User className="h-3 w-3 mr-1" />
                                  Normal
                                </>
                              )}
                            </span>
                          </td>
                          {/* <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${formattedCustomer.loyaltyTier.color}`}>
                              <Award className="h-3 w-3 mr-1" />
                              {formattedCustomer.loyaltyTier.name}
                            </span>
                          </td> */}
                          {/* <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-gray-900 font-medium">{customer.totalOrders || 0}</span>
                          </td> */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleToggleStatus(customer)}
                              disabled={actionLoading[customer._id]}
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border transition-all duration-200 ${
                                customer.isActive 
                                  ? 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200' 
                                  : 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200'
                              } ${
                                actionLoading[customer._id] 
                                  ? 'opacity-50 cursor-not-allowed' 
                                  : 'cursor-pointer hover:opacity-80'
                              }`}
                            >
                              {actionLoading[customer._id] && (
                                <ButtonSpinner />
                              )}
                              {/* {customer.isActive ? (
                                <CheckCircle className="h-3 w-3 mr-1" />
                              ) : (
                                <XCircle className="h-3 w-3 mr-1" />
                              )} */}
                              {customer.isActive ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center space-x-1">
                              <button
                                onClick={() => handleViewCustomer(customer)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                                title="View customer"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleEditCustomer(customer)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                                title="Edit customer"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteCustomer(customer)}
                                className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors"
                                title="Delete customer"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4 p-4">
                {paginatedCustomers.map((customer) => {
                  const formattedCustomer = CustomerService.formatCustomerForDisplay(customer)
                  return (
                    <Card key={customer._id} className="shadow-sm hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          {/* Customer Header */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-orange-100 rounded-lg">
                                <User className="h-4 w-4 text-orange-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{customer.name}</p>
                                <p className="text-sm text-gray-500">ID: {customer._id.slice(-8)}</p>
                              </div>
                            </div>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                              customer.isActive 
                                ? 'bg-green-100 text-green-800 border-green-200' 
                                : 'bg-gray-100 text-gray-800 border-gray-200'
                            }`}>
                              {customer.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>

                          {/* Customer Details */}
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center space-x-2">
                              <Phone className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900">{formattedCustomer.formattedMobile}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Building className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900">{customer.branch?.name || 'Not assigned'}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900">
                                {formattedCustomer.age ? `${formattedCustomer.age} years` : 'N/A'}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                customer.customerType === 'vip'
                                  ? 'bg-gradient-to-r from-yellow-100 to-amber-100 text-amber-800 border border-amber-200'
                                  : 'bg-blue-100 text-blue-800 border border-blue-200'
                              }`}>
                                {customer.customerType === 'vip' ? (
                                  <>
                                    <Award className="h-3 w-3 mr-1" />
                                    VIP
                                  </>
                                ) : (
                                  <>
                                    <User className="h-3 w-3 mr-1" />
                                    Normal
                                  </>
                                )}
                              </span>
                            </div>
                          </div>

                          {/* Loyalty Badge */}
                          <div className="flex items-center justify-between">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${formattedCustomer.loyaltyTier.color}`}>
                              <Award className="h-3 w-3 mr-1" />
                              {formattedCustomer.loyaltyTier.name}
                            </span>

                            {/* Action Buttons */}
                            <div className="flex items-center space-x-2">
                              <Button
                                onClick={() => handleViewCustomer(customer)}
                                variant="outline"
                                size="sm"
                                className="p-2"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                onClick={() => handleEditCustomer(customer)}
                                variant="outline"
                                size="sm"
                                className="p-2"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                onClick={() => handleDeleteCustomer(customer)}
                                variant="outline"
                                size="sm"
                                className="p-2 text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>

              {/* Pagination Bar */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <p className="text-sm text-gray-600">
                    Page <span className="font-semibold text-gray-900">{currentPage}</span> of <span className="font-semibold text-gray-900">{totalPages}</span>
                    {' '}·{' '}
                    <span className="font-semibold text-gray-900">{filteredCustomers.length}</span> customers total
                  </p>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      className="px-2 py-1.5 text-xs rounded border border-gray-300 text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      First
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>

                    {/* Page number pills */}
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...')
                        acc.push(p)
                        return acc
                      }, [])
                      .map((item, idx) =>
                        item === '...' ? (
                          <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 text-sm">…</span>
                        ) : (
                          <button
                            key={item}
                            onClick={() => handlePageChange(item)}
                            className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                              item === currentPage
                                ? 'bg-orange-600 text-white border-orange-600 font-semibold'
                                : 'border-gray-300 text-gray-600 hover:bg-white'
                            }`}
                          >
                            {item}
                          </button>
                        )
                      )
                    }

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-2 py-1.5 text-xs rounded border border-gray-300 text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <AddCustomerModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCustomerCreated={handleCustomerCreated}
        restrictedBranchId={getUserBranchId()}
        isBranchRestricted={isBranchRestricted()}
      />

      <EditCustomerModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        customer={selectedCustomer}
        onCustomerUpdated={handleCustomerUpdated}
        restrictedBranchId={getUserBranchId()}
        isBranchRestricted={isBranchRestricted()}
      />

      <ViewCustomerModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        customer={selectedCustomer}
      />

      <DeleteCustomerModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        customer={selectedCustomer}
        onCustomerDeleted={handleCustomerDeleted}
      />
    </div>
  )
}